import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { type OrgMember } from "./useOrganizationManagement";

interface OrganizationInvitation {
  id: string;
  organization_id: string;
  email: string;
  role: "admin" | "member";
  invited_by: string;
  expires_at: string;
  used_at: string | null;
  token: string;
  created_at: string;
}

interface UseRealtimeOrganizationDataProps {
  organizationId: string | undefined;
  initialMembers?: OrgMember[];
  initialInvitations?: any[];
}

export function useRealtimeOrganizationData({
  organizationId,
  initialMembers = [],
  initialInvitations = []
}: UseRealtimeOrganizationDataProps) {
  const [members, setMembers] = useState<OrgMember[]>(initialMembers);
  const [invitations, setInvitations] = useState<any[]>(initialInvitations);

  // Synchroniser les états initiaux
  useEffect(() => {
    setMembers(initialMembers);
  }, [initialMembers]);

  useEffect(() => {
    setInvitations(initialInvitations);
  }, [initialInvitations]);

  // Écouter les changements en temps réel
  useEffect(() => {
    if (!organizationId) return;

    console.log("🔄 Configuration du realtime pour l'organisation:", organizationId);

    // Canal pour les membres
    const membersChannel = supabase
      .channel(`organization-members-${organizationId}`)
      .on(
        "postgres_changes",
        { 
          event: "*", 
          schema: "public", 
          table: "organization_members", 
          filter: `organization_id=eq.${organizationId}` 
        },
        async (payload) => {
          console.log("🔄 Changement détecté sur organization_members:", payload);
          
          // Recharger les membres depuis la base de données
          const { data: freshMembers, error } = await supabase
            .from("organization_members")
            .select("*")
            .eq("organization_id", organizationId);
          
          if (!error && freshMembers) {
            // Enrichir avec les profils
            const userIds = freshMembers.map(m => m.user_id).filter((id): id is string => !!id);
            let profilesById: Record<string, any> = {};

            if (userIds.length > 0) {
              const { data: profiles } = await supabase
                .from("profiles")
                .select("id, email, first_name, last_name, company, position")
                .in("id", userIds);

              profilesById = (profiles || []).reduce((acc, p) => {
                acc[p.id] = p;
                return acc;
              }, {});
            }

            const withProfiles: OrgMember[] = freshMembers.map((m: any) => ({
              ...m,
              profile: m.user_id ? profilesById[m.user_id] ?? null : null,
            }));

            setMembers(withProfiles);
          }
        }
      )
      .subscribe();

    // Canal pour les invitations
    const invitationsChannel = supabase
      .channel(`organization-invitations-${organizationId}`)
      .on(
        "postgres_changes",
        { 
          event: "*", 
          schema: "public", 
          table: "organization_invitations", 
          filter: `organization_id=eq.${organizationId}` 
        },
        async (payload) => {
          console.log("🔄 Changement détecté sur organization_invitations:", payload);
          
          // Recharger les invitations depuis la base de données
          const { data: freshInvitations, error } = await supabase
            .from("organization_invitations")
            .select("*")
            .eq("organization_id", organizationId)
            .is("used_at", null)
            .order("created_at", { ascending: false });

          if (!error && freshInvitations) {
            setInvitations(freshInvitations);
          }
        }
      )
      .subscribe();

    return () => {
      console.log("🔄 Nettoyage des canaux realtime");
      supabase.removeChannel(membersChannel);
      supabase.removeChannel(invitationsChannel);
    };
  }, [organizationId]);

  return {
    members,
    invitations,
    setMembers,
    setInvitations
  };
}